import { apiClient } from '../../core/api';
import { busiestTimeDateRangePresets } from './dateRangePresets';

export interface BusiestTimeRecord {
  datePart: string;
  timePart: string;
  timezone: string;
}

export interface BusiestTimeFilters {
  startTime: string; // YYYY-MM-DD format
  endTime: string; // YYYY-MM-DD format
  userId: string;
  timezone?: string; // Optional timezone filter
}

interface BusiestTimeGraphQLResponse {
  data: {
    busiestTimesV2: {
      nodes: BusiestTimeRecord[];
      __typename: string;
    };
  };
}

export interface TimeSlotData {
  hour: string;
  day: string;
  traffic: 'light' | 'medium' | 'heavy';
  value: number;
}

export type TimePeriod = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'last3Months' | 'last12Months' | 'custom';

export interface DateBasedTimeSlotData {
  hour: string;
  dateLabel: string; // Full label for PC (e.g., "Monday", "Jan 1")
  dateLabelShort: string; // Short label for mobile (e.g., "Mon", "Jan 1")
  dateKey: string; // Raw date key for sorting
  traffic: 'light' | 'medium' | 'heavy';
  value: number;
}

export interface DateLabelPair {
  full: string;
  short: string;
}

export class BusiestTimeService {
  /**
   * Get raw busiest times data from GraphQL
   */
  static async getRawBusiestTimes(filters: Omit<BusiestTimeFilters, 'timezone'>): Promise<BusiestTimeRecord[]> {
    const query = `
      query GetBusiestTimes($startTime: Date!, $endTime: Date!, $userId: String!) {
        busiestTimesV2(startTime: $startTime, endTime: $endTime, userId: $userId) {
          nodes {
            datePart
            timePart
            timezone
            __typename
          }
          __typename
        }
      }
    `;

    try {
      const payload = {
        operationName: 'GetBusiestTimes',
        variables: {
          startTime: filters.startTime,
          endTime: filters.endTime,
          userId: filters.userId,
        },
        query,
      };

      const response = await apiClient.graphql<BusiestTimeGraphQLResponse['data']>(
        payload,
        '/graphql'
      );

      // Optional-chained like insightsService: a no-data/no-errors envelope means "no rows".
      return response.data?.busiestTimesV2?.nodes ?? [];
    } catch (error) {
      console.error('Failed to fetch busiest times:', error);
      throw error;
    }
  }

  /**
   * Get busiest times data from GraphQL (legacy method for backward compatibility)
   */
  static async getBusiestTimes(filters: BusiestTimeFilters): Promise<TimeSlotData[]> {
    const query = `
      query GetBusiestTimes($startTime: Date!, $endTime: Date!, $userId: String!) {
        busiestTimesV2(startTime: $startTime, endTime: $endTime, userId: $userId) {
          nodes {
            datePart
            timePart
            timezone
            __typename
          }
          __typename
        }
      }
    `;

    try {
      const payload = {
        operationName: 'GetBusiestTimes',
        variables: {
          startTime: filters.startTime,
          endTime: filters.endTime,
          userId: filters.userId,
        },
        query,
      };

      const response = await apiClient.graphql<BusiestTimeGraphQLResponse['data']>(
        payload,
        '/graphql'
      );

      const nodes = response.data?.busiestTimesV2?.nodes ?? [];
      
      // Filter by timezone if specified
      const filteredNodes = filters.timezone && filters.timezone !== 'all' 
        ? nodes.filter(record => record.timezone === filters.timezone)
        : nodes;

      return this.transformBusiestTimeData(filteredNodes);
    } catch (error) {
      console.error('Failed to fetch busiest times:', error);
      throw error;
    }
  }

  /**
   * Transform GraphQL response to heatmap format
   */
  static transformBusiestTimeData(records: BusiestTimeRecord[]): TimeSlotData[] {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const hours: string[] = [];
    for (let i = 0; i < 24; i++) {
      hours.push(`${i.toString().padStart(2, '0')}:00`);
    }

    // Count occurrences by hour of day and day of week
    const heatmapData: { [key: string]: number } = {};

    // Initialize all slots with 0
    days.forEach(day => {
      hours.forEach(hour => {
        const key = `${day}-${hour}`;
        heatmapData[key] = 0;
      });
    });

    // Process records - count the number of events per hour slot
    records.forEach(record => {
      // Parse date in the timezone it was recorded (for correct day of week)
      const date = new Date(record.datePart + 'T' + record.timePart);
      const dayOfWeek = days[date.getDay()];
      const timeParts = record.timePart.split(':');
      const hour = parseInt(timeParts[0], 10);

      // Use exact hour (no bucketing)
      const hourKey = `${hour.toString().padStart(2, '0')}:00`;

      const key = `${dayOfWeek}-${hourKey}`;
      if (heatmapData[key] !== undefined) {
        heatmapData[key]++;
      }
    });

    // Find the maximum count value to calculate dynamic thresholds
    const counts = Object.values(heatmapData);
    const maxCount = Math.max(...counts, 1); // Ensure at least 1 to avoid division by zero

    // Calculate dynamic thresholds based on percentages of max value
    const lightThreshold = Math.ceil(maxCount * 0.33); // 33% of max
    const mediumThreshold = Math.ceil(maxCount * 0.66); // 66% of max

    // Convert to TimeSlotData format
    const result: TimeSlotData[] = [];

    days.forEach(day => {
      hours.forEach(hour => {
        const key = `${day}-${hour}`;
        const count = heatmapData[key];

        // Dynamically determine traffic levels based on calculated thresholds
        let traffic: 'light' | 'medium' | 'heavy';
        if (count === 0 || count <= lightThreshold) {
          traffic = 'light';
        } else if (count <= mediumThreshold) {
          traffic = 'medium';
        } else {
          traffic = 'heavy';
        }

        result.push({
          hour,
          day,
          traffic,
          value: count, // Use actual count instead of normalized value
        });
      });
    });

    return result;
  }

  /**
   * Determine the grouping strategy based on date range
   */
  static getGroupingStrategy(startTime: string, endTime: string): 'weekday' | 'day' | 'month' {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 7) {
      return 'weekday'; // 1-7 days - show weekday names
    } else if (diffDays <= 60) {
      return 'day'; // 8-60 days - show each day
    } else {
      return 'month'; // 3+ months - show months
    }
  }

  /**
   * Format date label based on grouping strategy
   * Returns { full, short } for responsive display
   */
  static formatDateLabel(date: Date, grouping: 'weekday' | 'day' | 'month'): { full: string; short: string } {
    switch (grouping) {
      case 'weekday':
        return {
          full: date.toLocaleDateString('en-US', { weekday: 'long' }),
          short: date.toLocaleDateString('en-US', { weekday: 'short' }),
        };
      case 'day': {
        // Shorter format: "12/7" instead of "Dec 7" to prevent wrapping
        const dayFull = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
        return { full: dayFull, short: dayFull };
      }
      case 'month': {
        const monthLabel = date.toLocaleDateString('en-US', { month: 'short' });
        return { full: monthLabel, short: monthLabel };
      }
    }
  }

  /**
   * Get date key for grouping
   */
  static getDateKey(date: Date, grouping: 'weekday' | 'day' | 'month'): string {
    switch (grouping) {
      case 'weekday':
      case 'day':
        return date.toISOString().split('T')[0];
      case 'month':
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    }
  }

  /**
   * Transform GraphQL response to date-based heatmap format
   */
  static transformToDateBasedData(
    records: BusiestTimeRecord[],
    startTime: string,
    endTime: string
  ): { data: DateBasedTimeSlotData[]; dateLabels: DateLabelPair[]; grouping: 'weekday' | 'day' | 'month' } {
    const grouping = this.getGroupingStrategy(startTime, endTime);
    const hours: string[] = [];
    for (let i = 0; i < 24; i++) {
      hours.push(`${i.toString().padStart(2, '0')}:00`);
    }

    // X-axis is date/weekday/month
    const dateKeys: Map<string, DateLabelPair> = new Map();
    const start = new Date(startTime);
    const end = new Date(endTime);

    const current = new Date(start);
    while (current <= end) {
      const key = this.getDateKey(current, grouping);
      if (!dateKeys.has(key)) {
        dateKeys.set(key, this.formatDateLabel(current, grouping));
      }
      current.setDate(current.getDate() + 1);
    }

    const sortedDateKeys = Array.from(dateKeys.keys()).sort();
    const dateLabels = sortedDateKeys.map(key => dateKeys.get(key)!);

    // Count occurrences by hour and date
    const heatmapData: { [key: string]: number } = {};

    sortedDateKeys.forEach(dateKey => {
      hours.forEach(hour => {
        const key = `${dateKey}-${hour}`;
        heatmapData[key] = 0;
      });
    });

    records.forEach(record => {
      const date = new Date(record.datePart + 'T' + record.timePart);
      const dateKey = this.getDateKey(date, grouping);
      const timeParts = record.timePart.split(':');
      const hour = parseInt(timeParts[0], 10);
      const hourKey = `${hour.toString().padStart(2, '0')}:00`;

      const key = `${dateKey}-${hourKey}`;
      if (heatmapData[key] !== undefined) {
        heatmapData[key]++;
      }
    });

    const counts = Object.values(heatmapData);
    const maxCount = Math.max(...counts, 1);
    const lightThreshold = Math.ceil(maxCount * 0.33);
    const mediumThreshold = Math.ceil(maxCount * 0.66);

    const result: DateBasedTimeSlotData[] = [];

    sortedDateKeys.forEach(dateKey => {
      const labelPair = dateKeys.get(dateKey)!;
      hours.forEach(hour => {
        const key = `${dateKey}-${hour}`;
        const count = heatmapData[key];

        let traffic: 'light' | 'medium' | 'heavy';
        if (count === 0 || count <= lightThreshold) {
          traffic = 'light';
        } else if (count <= mediumThreshold) {
          traffic = 'medium';
        } else {
          traffic = 'heavy';
        }

        result.push({
          hour,
          dateLabel: labelPair.full,
          dateLabelShort: labelPair.short,
          dateKey,
          traffic,
          value: count,
        });
      });
    });

    return { data: result, dateLabels, grouping };
  }

  /**
   * Extract unique timezones from busiest time records
   */
  static extractAvailableTimezones(records: BusiestTimeRecord[]): string[] {
    const uniqueTimezones = new Set(records.map(record => record.timezone));
    return Array.from(uniqueTimezones).sort();
  }

  /**
   * Get date range presets
   */
  /**
   * Kept as the public entry point; the arithmetic itself lives in ./dateRangePresets so
   * the stores can read it WITHOUT importing this service (which pulls in core/api →
   * authStore → the stores again). See that file for the cycle it breaks.
   */
  static getDateRangePresets(): { [key: string]: { startTime: string; endTime: string } } {
    return busiestTimeDateRangePresets();
  }
}
